/**
 * Dependencies.
 */
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

import DriverModel from '@/api/driver/driver.model';

/**
 * Component.
 */
@Component({ name: 'status-on-the-way-to-service-point-component' })
export default class extends Vue {
  /**
   * Property `timer`.
   */
  timer = null;

  /**
   * Property `from`.
   */
  from = null;

  /**
   * Property `to`.
   */
  to = null;

  /**
   * Property `windowHeight`;
   */
  windowHeight = null;

  /**
   * Property `betweenFromDate`.
   */
  @Prop({ type: String })
  betweenFromDate: string;

  /**
   * Property `betweenToDate`.
   */
  @Prop({ type: String })
  betweenToDate: string;

  /**
   * Property `client`.
   */
  @Prop({ type: String })
  client: string;

  /**
   * Property `driver`.
   */
  @Prop({ type: Object })
  driver: DriverModel;

  /**
   * Property `servicePoint`.
   */
  @Prop({ default: null, type: String })
  servicePoint: string;

  /**
   * Property `orderType`.
   */
  @Prop({ default: null, type: String })
  orderType: string;

  /**
   * Property `numberOfPackages`.
   */
  @Prop({ default: 0, type: Number })
  numberOfPackages: number;

  /**
   * Getter `isDelivery`
   */
  get isDelivery() {
    return this.orderType === 'DELIVERY';
  }

  /**
   * Getter status title.
   */
  get statusTitle() {
    return this.i18n('TRACKING.SENTENCES.STATUS_HELLO', false, 'upperFirst', {
      name: this.client || '-'
    });
  }

  /**
   * Getter status sentence.
   */
  get statusSentence() {
    const { servicePoint = null } = this || {};
    const { name: driverName = null } = this.driver || {};

    const message = this.i18n(
      `TRACKING.SENTENCES.STATUS_${
        this.isDelivery ? 'DELIVERY' : 'COLLECT'
      }_ON_THE_WAY_TO_SERVICE_POINT`,
      false,
      'plain',
      {
        driver: `<b>${driverName || '-'}</b>`,
        from: `<b>${this.fromDeliveryDate}</b>`,
        name: `<b>${servicePoint || '-'}</b>`,
        packages:
          this.numberOfPackages > 1
            ? this.i18n(
              'TRACKING.SENTENCES.NUMBER_OF_PACKAGES',
              false,
              'plain',
              { total: this.numberOfPackages }
            )
            : '',
        to: `<b>${this.toDeliveryDate}</b>`
      }
    );

    return message;
  }

  /**
   * Getter `fromDeliveryDate`.
   */
  get fromDeliveryDate() {
    return this.from;
  }

  /**
   * Getter `toDeliveryDate`.
   */
  get toDeliveryDate() {
    return this.to;
  }

  /**
   * Getter `extraSmallScreenHeight`.
   */
  get extraSmallScreenHeight() {
    return this.windowHeight <= 320;
  }

  /**
   * Lifecycle mounted hook.
   */
  mounted() {
    this.from = this.betweenFromDate
      ? this.$moment(this.betweenFromDate).format('HH:mm')
      : '-';

    this.to = this.betweenToDate
      ? this.$moment(this.betweenToDate).format('HH:mm')
      : '-';

    this.handleFromToDate();
    this.timer = setInterval(this.handleFromToDate, 600000);

    this.handleOnResize();

    this.$nextTick(() => {
      window.addEventListener('resize', this.handleOnResize);
    });
  }

  /**
   * Lifecycle destroyed hook.
   */
  destroyed() {
    clearInterval(this.timer);
    window.removeEventListener('resize', this.handleOnResize);
  }

  /**
   * Handle to date.
   */
  handleFromToDate() {
    const to = this.$moment(this.betweenToDate);
    const now = this.$moment();
    const isNowAfterTo = now.isAfter(to);

    if (isNowAfterTo) {
      this.from = this.$moment()
        .subtract(5, 'minutes')
        .format('HH:mm');
      this.to = this.$moment()
        .add(10, 'minutes')
        .format('HH:mm');
    }
  }

  /**
   * Handle on resize.
   */
  handleOnResize() {
    this.windowHeight = window.innerHeight;
  }

  /**
   * Handle window height.
   */
  @Watch('windowHeight')
  handlewindowHeight(value) {
    this.windowHeight = value;
  }
}
